import React, { useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

import AppState from "../../store/AppState";
import {
  selectAnalyticsStartDate,
  selectAnalyticsEndDate,
  selectProductCodes,
  selectDateRange,
  selectProductAnalyticsIds,
} from "../../store/product/ProductSelectors";
import { loadProductAnalytics } from "../../store/product/ProductActions";
import { selectOverlay } from "../../store/overlay/OverlaySelectors";
import { selectIsModalOpen } from "../../store/modal/ModalSelectors";
import { selectCurrentStoreId } from "../../store/store-list/StoreListSelectors";
import { OVERLAY_TABS } from "../../utils/Constants";
import { heatMapModalId } from "../heat-map-modal/HeatMapModal";
import {
  selectSortingRule,
  selectSortingRulesList,
} from "../../store/product-list/ProductListSelectors";
import { BusinessRulesListDetails } from "../../store/product-list/ProductListTypes";
import { calculateStartEndDate } from "../../utils/ProductUtil";
import { selectSystemGeneratedRules } from "../../store/businessRules-list/BusinessRuleslistSelectors";
import { selectCurrentCategory } from "../../store/category/CategorySelectors";

const AnalyticsOverlayLabel = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const overlay = useSelector(selectOverlay);
  const startDate = useSelector(selectAnalyticsStartDate);
  const endDate = useSelector(selectAnalyticsEndDate);
  const productCodes = useSelector(selectProductCodes);
  const productAnalyticsIds = useSelector(selectProductAnalyticsIds);
  const storeId = useSelector(selectCurrentStoreId) || "";
  const selectedDateRange = useSelector(selectDateRange);
  const sortingRulesList = useSelector(selectSortingRulesList);
  const systemGeneratedRulesList = useSelector(selectSystemGeneratedRules);
  const allSortingRulesList = useMemo(
    () => [...systemGeneratedRulesList, ...sortingRulesList],
    [systemGeneratedRulesList, sortingRulesList],
  );

  const selectedRule = useSelector(selectSortingRule);
  const currentCategory = useSelector(selectCurrentCategory);
  const isHeatMapModalOpen = useSelector((state: AppState) =>
    selectIsModalOpen(state, heatMapModalId),
  );
  // Source-of-truth cache for "what analytics data is actually in the
  // store right now". We reconcile `fetchedIdsRef` against this so a
  // failed/cancelled request can never permanently mark IDs as fetched.
  const productAnalyticsCache = useSelector(
    (state: AppState) => state.products.analytics.productAnalytics,
  );

  // Create stable keys for array comparison (compare content, not reference)
  const productAnalyticsIdsKey = useMemo(() => {
    return JSON.stringify([...productAnalyticsIds].sort());
  }, [productAnalyticsIds]);

  /**
   * Delta-fetch state:
   * - `inFlightIdsRef` — analytics IDs whose request is currently in
   *   flight, keyed to the dispatch timestamp. The timestamp lets us
   *   evict stuck-forever entries after `IN_FLIGHT_TIMEOUT_MS` so a
   *   cancelled / failed request doesn't permanently block a retry.
   *   "Already loaded" is read from the Redux analytics cache directly
   *   rather than tracked locally, so we never desync from the store.
   * - `fetchedRangeKeyRef` — `start|end` of the range whose IDs are
   *   tracked in `inFlightIdsRef`. When the date range changes, the set
   *   is cleared so the next dispatch covers the full current product
   *   list under the new range.
   * - `lastCategoryRef` — resets the set when the user navigates to a
   *   different category.
   */
  const IN_FLIGHT_TIMEOUT_MS = 30_000;
  const inFlightIdsRef = useRef<Map<string, number>>(new Map());
  const fetchedRangeKeyRef = useRef<string>("");
  const lastCategoryRef = useRef<string | null>(null);

  // Reset when category changes
  useEffect(() => {
    if (lastCategoryRef.current !== currentCategory) {
      inFlightIdsRef.current = new Map();
      fetchedRangeKeyRef.current = "";
      lastCategoryRef.current = currentCategory;
    }
  }, [currentCategory]);

  // Belt-and-braces: a fresh open of the Analytics overlay should never
  // trust a previously-populated ref (e.g. re-clicking the same category
  // doesn't change `currentCategory` so the cleanup above doesn't fire).
  useEffect(() => {
    if (overlay === OVERLAY_TABS.ANALYTICS_OVERLAY) {
      inFlightIdsRef.current = new Map();
      fetchedRangeKeyRef.current = "";
    }
  }, [overlay]);

  useEffect(
    () => {
      if (
        overlay !== OVERLAY_TABS.ANALYTICS_OVERLAY ||
        productCodes.length === 0 ||
        isHeatMapModalOpen
      ) {
        return;
      }

      // Resolve the requested date range — sorting-rule overrides win
      // over the user-selected date range in the picker.
      const sortingRule = allSortingRulesList?.find(
        (rule) => rule.ruleId === selectedRule,
      );
      let nextStartDate: string | undefined;
      let nextEndDate: string | undefined;

      if (sortingRule) {
        const range = calculateStartEndDate(
          sortingRule as BusinessRulesListDetails,
        );
        nextStartDate = range.startDate;
        nextEndDate = range.endDate;
      } else {
        nextStartDate = selectedDateRange.startDate;
        nextEndDate = selectedDateRange.endDate;
      }

      if (!nextStartDate || !nextEndDate) return;

      // If the requested range differs from what's already tracked,
      // clear the in-flight map so the upcoming dispatch covers the
      // full current ID list under the new range.
      const rangeKey = `${nextStartDate}|${nextEndDate}`;
      if (rangeKey !== fetchedRangeKeyRef.current) {
        inFlightIdsRef.current = new Map();
        fetchedRangeKeyRef.current = rangeKey;
      }

      const cacheIsFreshForRange =
        nextStartDate === startDate && nextEndDate === endDate;

      // Reconcile the in-flight map: an entry should be cleared once
      // either (a) its analytics data has landed in the Redux cache
      // for the *current* range (SUCCESS) or (b) the request has been
      // pending long enough that we should treat it as failed and
      // allow a retry. We deliberately do NOT prune entries whose data
      // is simply not in the cache yet — that's an in-flight request
      // waiting for SUCCESS, and pruning would cause the same IDs to
      // be re-dispatched on every render, producing parallel duplicate
      // API calls.
      const now = Date.now();
      for (const [id, ts] of Array.from(inFlightIdsRef.current.entries())) {
        const settled =
          cacheIsFreshForRange && Boolean(productAnalyticsCache[id]);
        const timedOut = now - ts > IN_FLIGHT_TIMEOUT_MS;
        if (settled || timedOut) {
          inFlightIdsRef.current.delete(id);
        }
      }

      // Compute the delta — IDs that aren't already loaded in the
      // store *for the current range* and aren't currently in flight.
      // Dedup with a Set so a single request never carries the same
      // productCode twice (defensive: the selector can yield duplicates
      // if products share an analyticsId, and earlier renders may have
      // been firing duplicate dispatches into the same payload).
      const newIds = Array.from(
        new Set(
          productAnalyticsIds.filter(
            (id) =>
              !(cacheIsFreshForRange && productAnalyticsCache[id]) &&
              !inFlightIdsRef.current.has(id),
          ),
        ),
      );
      if (newIds.length === 0) return;

      newIds.forEach((id) => inFlightIdsRef.current.set(id, now));

      dispatch(
        loadProductAnalytics({
          startDate: nextStartDate,
          endDate: nextEndDate,
          productCodes: newIds,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      productAnalyticsIdsKey,
      overlay,
      startDate,
      endDate,
      dispatch,
      storeId,
      selectedDateRange,
      selectedRule,
      allSortingRulesList,
      productAnalyticsCache,
      isHeatMapModalOpen,
    ],
  );

  return (
    <>
      {intl.formatMessage({
        id: "overlayTabs.analyticsLabel",
        defaultMessage: "Analytics",
      })}
    </>
  );
};

export default React.memo(AnalyticsOverlayLabel);
